/** EPN-236 / EPN-237 — the typed payslip status model shared by the Employee Payslips list and the Earning Statement.
 *
 * The approved Figma shows four statuses: Approved, Processing, Paid and Voided. Since EPN-237 `EPN.Reports.Api`
 * derives them from existing facts (Approved run; a Submitted payment batch; a Settled payment batch; a payslip
 * reversal). A Paid that came from a mock ACH provider arrives with `simulated: true` and is labelled
 * "Paid (simulated)" (`payslipStatusLabel`): plain "Paid" is shown only for a non-simulated settlement, which
 * cannot exist until a live provider does. See docs/employee-payslips-admin.md.
 *
 * Safety rules encoded here:
 *  - Approved is never presented as Paid.
 *  - A status string this UI does not recognise degrades to a neutral chip carrying the raw label; it can
 *    never be mistaken for Paid (or any other known state).
 *  - Status is always conveyed by text and an icon, never by colour alone. */
export const PAYSLIP_STATUSES = ['Approved', 'Processing', 'Paid', 'Voided'] as const;
export type PayslipStatus = (typeof PAYSLIP_STATUSES)[number];

/** Case-insensitive, whitespace-tolerant parse. Returns null for anything that is not a known status. */
export function parsePayslipStatus(raw: string | null | undefined): PayslipStatus | null {
  const value = (raw ?? '').trim().toLowerCase();
  return PAYSLIP_STATUSES.find((status) => status.toLowerCase() === value) ?? null;
}

export type PayslipStatusTone = 'approved' | 'processing' | 'paid' | 'voided' | 'unknown';

export interface PayslipStatusView {
  /** The known status, or null when the wire value is not recognised. */
  status: PayslipStatus | null;
  /** Text shown in the chip: the known label, or the raw wire value (never "Paid") when unrecognised. */
  label: string;
  tone: PayslipStatusTone;
}

export function payslipStatusView(raw: string | null | undefined): PayslipStatusView {
  const status = parsePayslipStatus(raw);
  if (status) return { status, label: status, tone: status.toLowerCase() as PayslipStatusTone };
  const trimmed = (raw ?? '').trim();
  return { status: null, label: trimmed === '' ? 'Unknown' : trimmed, tone: 'unknown' };
}

/** Wording for the dated note under a statement's status badge. Only rendered when the event date really exists. */
export const PAYSLIP_STATUS_NOTE_PREFIX: Record<PayslipStatus, string> = {
  Approved: 'Approved on',
  Processing: 'Processing since',
  Paid: 'Paid on',
  Voided: 'Voided on',
};

/** The text of a status chip. A simulated Paid is never labelled plain "Paid". Unknown statuses keep their raw label. */
export function payslipStatusLabel(raw: string | null | undefined, simulated = false): string {
  const view = payslipStatusView(raw);
  return view.status === 'Paid' && simulated ? 'Paid (simulated)' : view.label;
}

/** Wire value -> label for the Pay Method column. Null/empty is "---" (no payment or check batch names this employee yet);
 * an unrecognised value is shown as received rather than guessed at. */
export const PAY_METHOD_NONE = '---';
export const PAY_METHODS = ['DirectDeposit', 'Check'] as const;
export function payMethodLabel(raw: string | null | undefined): string {
  switch ((raw ?? '').trim()) {
    case '':
      return PAY_METHOD_NONE;
    case 'DirectDeposit':
      return 'Direct Deposit';
    case 'Check':
      return 'Check';
    default:
      return (raw ?? '').trim();
  }
}
